

import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
// Components
import Dashboard from "./components/Dashboard";
import Login from "./components/Login";
import TerminalPage from "./components/TerminalPage"; // New page for the terminal
import LoadingScreen from "./components/LoadingScreen";
import SingleTabGuard from "./components/SingleTabGuard";
import { useUser } from "./contexts/UserContext";
import { getCsrfToken, getRefreshToken } from './utils/auth';

// Lazy: this page pulls in the charting library, and only admins ever open
// it — no reason to put that in the bundle everyone downloads.
const Insights = lazy(() => import("./components/Insights"));

const App = () => {
  const { user, setUser, admin, setAdmin, company, setCompany, username, setUsername } = useUser();
  const [loading, setLoading] = useState(true);

  
  useEffect(() => {
    setLoading(true);

    async function initAuth() {
      try {
        let csrf = getCsrfToken();
        console.log("csrf: ", csrf)

        await fetch(`/api/token/refresh`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'X-CSRF-TOKEN': getRefreshToken() }
        });

        let res = await fetch(`/api/test-jwt`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'X-CSRF-TOKEN': csrf }
        });

        if (res.status === 401) {
          const refreshRes = await fetch(`/api/token/refresh`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'X-CSRF-TOKEN': csrf }
          });

          if (!refreshRes.ok) throw new Error('Refresh failed');

          csrf = getCsrfToken();
          console.log("csrf-2: ", csrf)

          res = await fetch(`/api/test-jwt`, {
            method: 'GET',
            credentials: 'include',
            headers: { 'X-CSRF-TOKEN': csrf }
          });
        }

        if (!res.ok) throw new Error('Not authenticated');

        const data = await res.json();
        console.log("data: ", data)
        const { identity, claims } = data;

        setUsername(identity);
        setCompany(claims.roles);
        setAdmin(claims.roles === 'admin');
        setUser(true);
      } catch (err) {
        console.warn('Auth check failed:', err);
        setUsername(null);
        setCompany(null);
        setAdmin(false);
        setUser(false);
      } finally {
        setLoading(false);
      }
    }

    initAuth();
  }, []);

  useEffect(() => {
    const slide = async () => {
      const csrf = getRefreshToken();
      const res = await fetch('/api/token/slide', {
        method: 'POST',
        credentials: 'include',
        headers: csrf ? { 'X-CSRF-TOKEN': csrf } : {},
      });
      if (!res.ok) {
        console.warn('slide failed', res.status, await res.text());
      }
    };
    slide();
  }, []);


  // Protected Route component
  const ProtectedRoute = ({ children }) => {
    if (loading) return <LoadingScreen />;
    return user ? children : <Navigate to="/login" />;
  };

  return (
    <SingleTabGuard>
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            user ? (
              <Navigate to="/dashboard" />
            ) : (
              <Login
                setUser={setUser}
                sessionToken={user}
                setAdmin={setAdmin}
                setCompany={setCompany}
              />
            )
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard
                user={user}
                admin={admin}
                setUser={setUser}
                setAdmin={setAdmin}
                company={company}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/insights"
          element={
            <ProtectedRoute>
              {/* Admin-only: it ranks customers against each other. */}
              {admin ? (
                <Suspense fallback={<LoadingScreen />}>
                  <Insights setUser={setUser} setAdmin={setAdmin} />
                </Suspense>
              ) : (
                <Navigate to="/dashboard" />
              )}
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" />} />
      </Routes>
    </Router>
    </SingleTabGuard>
  );
};

export default App;
